import React, { useState, useEffect, useCallback } from 'react';
import { getPayroll } from '../services/api';
import { Loader2, Calendar, FileText, Download, Wallet, TrendingUp, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Payroll = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [payrollData, setPayrollData] = useState([]);
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10)); // First day of current month
    const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10)); // Current date
    const [error, setError] = useState(null);

    const fetchPayroll = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await getPayroll(fromDate, toDate);
            if (res.success) {
                setPayrollData(res.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || t('payroll.messages.fetch_failed'));
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, t]);

    useEffect(() => {
        fetchPayroll();
    }, [fetchPayroll]);

    const handleExportCSV = () => {
        if (payrollData.length === 0) return;

        const headers = [
            t('payroll.export_headers.agent_name'),
            t('payroll.export_headers.payment_type'),
            t('payroll.export_headers.base_salary'),
            t('payroll.export_headers.delivered_orders'),
            t('payroll.export_headers.commission_per_order'),
            t('payroll.export_headers.commission_total'),
            t('payroll.export_headers.total_pay')
        ];
        const rows = payrollData.map(p => [
            p.agentName,
            t(`users.form.payment_types.${p.paymentType.toLowerCase()}`),
            p.monthlySalary,
            p.deliveredOrdersCount,
            p.commissionPerOrder,
            p.commissionAmount,
            p.totalPay
        ]);

        const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `payroll_${fromDate}_to_${toDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const totalPayout = payrollData.reduce((acc, curr) => acc + curr.totalPay, 0);
    const totalDelivered = payrollData.reduce((acc, curr) => acc + curr.deliveredOrdersCount, 0);

    return (
        <div className="px-4 py-6 md:p-8 w-full max-w-7xl mx-auto space-y-6 md:space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">{t('payroll.title')}</h1>
                    <p className="mt-1 text-xs md:text-sm text-gray-500 font-medium">{t('payroll.subtitle')}</p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
                    <div className="flex flex-col md:flex-row gap-3 w-full sm:w-auto">
                        <div className="relative flex-1 xs:w-40 md:w-auto">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                            <input
                                type="date"
                                className="w-full bg-white border border-gray-200 pl-9 pr-3 py-2.5 rounded-xl font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all shadow-sm text-xs md:text-sm"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>
                        <div className="relative flex-1 xs:w-40 md:w-auto">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                            <input
                                type="date"
                                className="w-full bg-white border border-gray-200 pl-9 pr-3 py-2.5 rounded-xl font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all shadow-sm text-xs md:text-sm"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>
                    </div>
                    <button
                        onClick={handleExportCSV}
                        disabled={payrollData.length === 0}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gray-900 hover:bg-black text-white px-5 py-3 md:py-2.5 rounded-xl transition-all font-bold text-xs md:text-sm shadow-sm disabled:opacity-50"
                    >
                        <Download size={18} />
                        {t('payroll.actions.export_csv')}
                    </button>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                <div className="bg-white p-5 md:p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 md:w-12 md:h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 shrink-0">
                            <Wallet size={22} className="md:w-6 md:h-6" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest truncate">{t('payroll.stats.total_payout')}</p>
                            <h3 className="text-xl md:text-2xl font-black text-gray-900 truncate">{totalPayout.toLocaleString()} {t('common.currency')}</h3>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-5 md:p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 md:w-12 md:h-12 bg-green-50 rounded-2xl flex items-center justify-center text-green-600 shrink-0">
                            <TrendingUp size={22} className="md:w-6 md:h-6" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest truncate">{t('payroll.stats.delivered_order_total')}</p>
                            <h3 className="text-xl md:text-2xl font-black text-gray-900 truncate">{totalDelivered}</h3>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-5 md:p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 md:w-12 md:h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 shrink-0">
                            <Users size={22} className="md:w-6 md:h-6" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest truncate">{t('payroll.stats.active_agents')}</p>
                            <h3 className="text-xl md:text-2xl font-black text-gray-900 truncate">{payrollData.length}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-12 md:p-20 flex flex-col items-center justify-center">
                    <Loader2 size={32} className="text-indigo-600 animate-spin mb-4 md:w-10 md:h-10" />
                    <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px] md:text-xs leading-relaxed text-center">{t('payroll.messages.loading')}</p>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-100 rounded-3xl p-6 md:p-8 text-center">
                    <p className="text-red-600 font-bold text-sm md:text-base">{error}</p>
                    <button
                        onClick={fetchPayroll}
                        className="mt-4 text-indigo-600 font-bold hover:underline text-sm"
                    >
                        {t('payroll.actions.try_again')}
                    </button>
                </div>
            ) : payrollData.length === 0 ? (
                <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-12 md:p-20 flex flex-col items-center justify-center text-center">
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-300 mb-4">
                        <FileText size={28} className="md:w-8 md:h-8" />
                    </div>
                    <h3 className="text-gray-900 font-bold text-base md:text-lg">{t('payroll.messages.no_data_title')}</h3>
                    <p className="text-gray-500 max-w-xs mt-1 text-xs md:text-sm">{t('payroll.messages.no_data_subtitle', { range: `${fromDate} - ${toDate}` })}</p>
                </div>
            ) : (
                <>
                    {/* Desktop View Table */}
                    <div className="hidden md:block bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="bg-gray-50/50 border-b border-gray-100">
                                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">{t('payroll.table.agent')}</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">{t('payroll.table.payment_structure')}</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">{t('payroll.table.delivered')}</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">{t('payroll.table.salary')}</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">{t('payroll.table.commission_total')}</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] text-right">{t('payroll.table.net_pay')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {payrollData.map((agent) => (
                                        <tr key={agent.agentId} className="hover:bg-gray-50/50 transition-colors group">
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 font-black text-sm uppercase">
                                                        {agent.agentName.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-900">{agent.agentName}</p>
                                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{t('payroll.table.agent_id', { id: agent.agentId.slice(-4) })}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border ${agent.paymentType === 'COMMISSION_ONLY'
                                                    ? 'bg-purple-50 text-purple-600 border-purple-100'
                                                    : agent.paymentType === 'SALARY_PLUS_COMMISSION'
                                                        ? 'bg-indigo-50 text-indigo-600 border-indigo-100'
                                                        : 'bg-blue-50 text-blue-600 border-blue-100'
                                                    }`}>
                                                    {t(`users.form.payment_types.${agent.paymentType.toLowerCase()}`)}
                                                </span>
                                            </td>
                                            <td className="px-8 py-6 text-center">
                                                <div className="inline-flex flex-col items-center">
                                                    <span className="text-sm font-black text-gray-900">{agent.deliveredOrdersCount}</span>
                                                    <span className="text-[9px] text-gray-400 font-bold uppercase">{t('payroll.table.orders_count')}</span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 font-bold text-gray-700">
                                                {agent.monthlySalary.toLocaleString()} {t('common.currency')}
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-indigo-600">+{agent.commissionAmount.toLocaleString()} {t('common.currency')}</span>
                                                    <span className="text-[9px] text-gray-400 font-medium">({agent.commissionPerOrder} / order)</span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 text-right">
                                                <span className="text-lg font-black text-gray-900">
                                                    {agent.totalPay.toLocaleString()} {t('common.currency')}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-4">
                        {payrollData.map((agent) => (
                            <div key={agent.agentId} className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm space-y-5">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 font-black text-sm uppercase shrink-0">
                                        {agent.agentName.charAt(0)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-black text-gray-900 text-sm truncate">{agent.agentName}</p>
                                        <p className="text-[8px] text-gray-400 font-black uppercase tracking-widest">{t('payroll.table.agent_id', { id: agent.agentId.slice(-4) })}</p>
                                    </div>
                                    <span className={`ml-auto px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest border shrink-0 ${agent.paymentType === 'COMMISSION_ONLY'
                                        ? 'bg-purple-50 text-purple-600 border-purple-100'
                                        : agent.paymentType === 'SALARY_PLUS_COMMISSION'
                                            ? 'bg-indigo-50 text-indigo-600 border-indigo-100'
                                            : 'bg-blue-50 text-blue-600 border-blue-100'
                                        }`}>
                                        {t(`users.form.payment_types.${agent.paymentType.toLowerCase()}`)}
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 gap-4 pt-1">
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">{t('payroll.table.delivered')}</p>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-sm font-black text-gray-900">{agent.deliveredOrdersCount}</span>
                                            <span className="text-[8px] text-gray-400 font-bold uppercase">{t('payroll.table.orders_count')}</span>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">{t('payroll.table.salary')}</p>
                                        <p className="text-sm font-bold text-gray-900">{agent.monthlySalary.toLocaleString()} {t('common.currency')}</p>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-gray-50 flex justify-between items-end">
                                    <div className="space-y-1">
                                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">{t('payroll.table.commission_total')}</p>
                                        <div className="flex flex-col">
                                            <span className="text-xs font-bold text-indigo-600">+{agent.commissionAmount.toLocaleString()} {t('common.currency')}</span>
                                            <span className="text-[8px] text-gray-400 font-medium">({agent.commissionPerOrder} / cmd)</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{t('payroll.table.net_pay')}</p>
                                        <span className="text-lg font-black text-gray-900">
                                            {agent.totalPay.toLocaleString()} {t('common.currency')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default Payroll;
